import { listEstablishmentsQueryOptions } from "@packages/api-client/hooks/establishmentController";
import { listSubObjectivesByObjectiveQueryOptions } from "@packages/api-client/hooks/planningTasksController";
import type {
	Objective,
	SubObjective,
	Task,
	TaskStatus,
} from "@packages/shared-types";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	ChevronRight,
	CloudOff,
	Layers,
	PanelLeftOpen,
	Plus,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	useDeleteObjective,
	useDeleteSubObjective,
} from "@/api/planning/objectives";
import { useUpdateTaskStatus } from "@/api/planning/tasks";
import {
	scopedObjectivesQueryOptions,
	scopedTasksQueryOptions,
} from "@/api/scopedQueries";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { OfflineUnavailable } from "@/components/OfflineUnavailable";
import {
	PlanningHierarchyTree,
	type PlanningView,
	PlanningViewMenu,
	PlanningViewSwitcher,
	TaskCalendar,
	TaskKanban,
	TaskList,
	type TreeSelection,
} from "@/components/planning";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { isAllFarms, useFarmScope } from "@/hooks/useFarmScope";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { getOfflineTaskRows } from "@/hooks/useTasksListData";
import { cn } from "@/utils/utils";
import { ObjectiveCreateDialog } from "./-components/ObjectiveCreateDialog";
import { ObjectiveEditDialog } from "./-components/ObjectiveEditDialog";
import { PlanningCreateSheet } from "./-components/PlanningCreateSheet";
import { SubObjectiveCreateDialog } from "./-components/SubObjectiveCreateDialog";
import { SubObjectiveEditDialog } from "./-components/SubObjectiveEditDialog";
import {
	type TaskCreateDefaults,
	TaskCreateDialog,
} from "./-components/TaskCreateDialog";
import { TaskEditDialog } from "./-components/TaskEditDialog";

type PlanningSearch = {
	view?: PlanningView;
	objective?: string;
	subObjective?: string;
	create?: "task" | "objective";
	farm?: string;
};

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/planning/")({
	// Offline the workspace degrades to a read-only task list from the prepared
	// tasks dataset (roadmap step 6): field workers see the day's tasks and open
	// each one to mark it complete. Objectives/dialogs/kanban are desk work and
	// only render online.
	staticData: { offline: "ready" },
	validateSearch: (search: Record<string, unknown>): PlanningSearch => ({
		view:
			search.view === "list" ||
			search.view === "calendar" ||
			search.view === "board"
				? search.view
				: undefined,
		objective:
			typeof search.objective === "string" ? search.objective : undefined,
		subObjective:
			typeof search.subObjective === "string" ? search.subObjective : undefined,
		create:
			search.create === "task" || search.create === "objective"
				? search.create
				: undefined,
		// Preserve the org-level farm scope param across this route's validation.
		farm: typeof search.farm === "string" ? search.farm : undefined,
	}),
	component: PlanningPage,
});

function PlanningPage() {
	const online = useOnlineStatus();
	return online ? <PlanningWorkspace /> : <PlanningOfflineTasks />;
}

/**
 * Offline workspace: a read-only task list from the prepared tasks
 * dataset, scoped to the active farm. Tapping a task opens the detail route
 * (offline there too), which carries the queueable "mark complete" action.
 * Creation/editing and the objectives hierarchy stay online-only desk work.
 */
function PlanningOfflineTasks() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const scope = useFarmScope();
	const queryClient = useQueryClient();

	const rows = getOfflineTaskRows(
		queryClient,
		orgId,
		scope.kind === "single" ? scope.establishmentId : undefined,
	);

	if (!rows) {
		return <OfflineUnavailable variant="preparable" />;
	}

	const farmNameMap: Record<string, string> = {};
	for (const row of rows) {
		if (row.establishment)
			farmNameMap[row.establishment.id] = row.establishment.name;
	}

	return (
		<div className="flex flex-col gap-4">
			{/* Same page title as online; the create action stays online-only. */}
			<h1 className="font-bold text-[12px] text-base-content/70 uppercase tracking-[0.14em]">
				{t("common.planning")}
			</h1>
			<div className="flex items-center gap-2 text-sm text-muted-foreground">
				<CloudOff className="h-4 w-4" />
				<span>{t("offline.detail.offlineNote")}</span>
			</div>
			<TaskList
				tasks={rows as Task[]}
				showCreateButton={false}
				farmNameMap={isAllFarms(scope) ? farmNameMap : undefined}
				emptyMessage={t("planning.tasks.emptyInScope")}
				onTaskClick={(task) =>
					navigate({
						to: "/app/org/$orgId/planning/tasks/$taskId",
						params: { orgId, taskId: task.id },
					})
				}
			/>
		</div>
	);
}

function PlanningWorkspace() {
	const { orgId } = Route.useParams();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const { t } = useTranslation();
	const scope = useFarmScope();

	const { objective: objectiveParam, subObjective: subObjectiveParam } = search;
	// `view` defaults to the board; it's optional in the URL so external links to
	// /planning (e.g. the quick-capture FAB) don't have to supply it.
	const view: PlanningView = search.view ?? "board";

	// ── Data ──────────────────────────────────────────────────────────────────
	const { data: farmsData } = useQuery(listEstablishmentsQueryOptions(orgId));
	const { data: objectivesData, isLoading: objectivesLoading } = useQuery(
		scopedObjectivesQueryOptions(orgId, scope),
	);
	const objectives = useMemo(
		() => (objectivesData ?? []) as Objective[],
		[objectivesData],
	);
	const { data: tasksData, isLoading: tasksLoading } = useQuery(
		scopedTasksQueryOptions(orgId, scope),
	);
	const tasks = useMemo(() => (tasksData?.data ?? []) as Task[], [tasksData]);

	// Sub-objectives for every objective, so the tree shows the full hierarchy and
	// accurate rolled-up counts up front (each query is cached and reused by the
	// create-task sub-objective picker below).
	const subObjectiveQueries = useQueries({
		queries: objectives.map((o) =>
			listSubObjectivesByObjectiveQueryOptions(orgId, o.id),
		),
	});

	const subObjectivesByObjective: Record<string, SubObjective[]> = {};
	objectives.forEach((o, i) => {
		subObjectivesByObjective[o.id] = (subObjectiveQueries[i]?.data ??
			[]) as SubObjective[];
	});
	const subToObjective: Record<string, string> = {};
	for (const [objId, subs] of Object.entries(subObjectivesByObjective)) {
		for (const sub of subs) subToObjective[sub.id] = objId;
	}

	// Tasks grouped under each level (for the tree leaves) + rolled-up counts.
	const tasksBySubObjective: Record<string, Task[]> = {};
	const directTasksByObjective: Record<string, Task[]> = {};
	for (const task of tasks) {
		if (task.subObjectiveId) {
			const list = tasksBySubObjective[task.subObjectiveId] ?? [];
			list.push(task);
			tasksBySubObjective[task.subObjectiveId] = list;
		} else if (task.objectiveId) {
			const list = directTasksByObjective[task.objectiveId] ?? [];
			list.push(task);
			directTasksByObjective[task.objectiveId] = list;
		}
	}
	const subObjectiveTaskCounts: Record<string, number> = {};
	for (const [id, list] of Object.entries(tasksBySubObjective)) {
		subObjectiveTaskCounts[id] = list.length;
	}
	const objectiveTaskCounts: Record<string, number> = {};
	for (const o of objectives) {
		const subTotal = (subObjectivesByObjective[o.id] ?? []).reduce(
			(acc, sub) => acc + (subObjectiveTaskCounts[sub.id] ?? 0),
			0,
		);
		objectiveTaskCounts[o.id] =
			(directTasksByObjective[o.id]?.length ?? 0) + subTotal;
	}

	// ── Selection (URL → typed scope) ──────────────────────────────────────────
	const selection: TreeSelection = subObjectiveParam
		? {
				kind: "subObjective",
				objectiveId: objectiveParam ?? subToObjective[subObjectiveParam] ?? "",
				subObjectiveId: subObjectiveParam,
			}
		: objectiveParam
			? { kind: "objective", objectiveId: objectiveParam }
			: { kind: "all" };

	// Tasks shown in the right pane, scoped to the selection. An objective scope
	// rolls up its direct tasks plus those on its sub-objectives.
	const filteredTasks =
		!objectiveParam && !subObjectiveParam
			? tasks
			: subObjectiveParam
				? tasks.filter((tk) => tk.subObjectiveId === subObjectiveParam)
				: tasks.filter(
						(tk) =>
							tk.objectiveId === objectiveParam ||
							(tk.subObjectiveId
								? subToObjective[tk.subObjectiveId] === objectiveParam
								: false),
					);

	// ── Display name maps (memoized to keep child views stable) ────────────────
	const farmNameMap = useMemo(() => {
		const map: Record<string, string> = {};
		for (const farm of farmsData?.data ?? []) map[farm.id] = farm.name;
		return map;
	}, [farmsData]);
	const objectiveNameMap = useMemo(() => {
		const map: Record<string, string> = {};
		for (const o of objectives) map[o.id] = o.name;
		return map;
	}, [objectives]);
	const paddockNameMap = useMemo(() => ({}) as Record<string, string>, []);

	// ── Tree expansion ─────────────────────────────────────────────────────────
	const [expanded, setExpanded] = useState<Set<string>>(
		() => new Set(objectiveParam ? [objectiveParam] : []),
	);
	const selectedObjectiveId =
		selection.kind === "all" ? undefined : selection.objectiveId;
	// Keep the selected objective open so its sub-objective stays visible.
	useEffect(() => {
		if (!selectedObjectiveId) return;
		setExpanded((prev) =>
			prev.has(selectedObjectiveId)
				? prev
				: new Set(prev).add(selectedObjectiveId),
		);
	}, [selectedObjectiveId]);

	const handleToggleExpand = (objectiveId: string) =>
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(objectiveId)) next.delete(objectiveId);
			else next.add(objectiveId);
			return next;
		});

	// ── Navigation (selection + view live in the URL) ──────────────────────────
	const [isTreeOpen, setIsTreeOpen] = useState(false);
	// Collapsed desktop hierarchy rail (gives the board full width), persisted.
	const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
		try {
			return localStorage.getItem("ruralops-planning-rail-collapsed") === "1";
		} catch {
			return false;
		}
	});
	useEffect(() => {
		try {
			localStorage.setItem(
				"ruralops-planning-rail-collapsed",
				railCollapsed ? "1" : "0",
			);
		} catch {
			// Ignore storage failures — collapse still works for the session.
		}
	}, [railCollapsed]);

	const handleSelect = (sel: TreeSelection) => {
		navigate({
			to: "/app/org/$orgId/planning",
			params: { orgId },
			search: (prev) => ({
				...prev,
				objective: sel.kind === "all" ? undefined : sel.objectiveId,
				subObjective:
					sel.kind === "subObjective" ? sel.subObjectiveId : undefined,
			}),
			replace: true,
		});
		if (sel.kind !== "all") {
			setExpanded((prev) => new Set(prev).add(sel.objectiveId));
		}
		setIsTreeOpen(false);
	};

	const handleViewChange = (next: PlanningView) => {
		navigate({
			to: "/app/org/$orgId/planning",
			params: { orgId },
			search: (prev) => ({ ...prev, view: next }),
			replace: true,
		});
	};

	// ── Mutations ──────────────────────────────────────────────────────────────
	// Create/update mutations for objectives, sub-objectives, and tasks now live
	// inside their own dialogs (forms standard §3.5 — one guarded submit each).
	// The workspace keeps only the delete + kanban-status flows.
	const deleteObjectiveMutation = useDeleteObjective();
	const deleteSubObjectiveMutation = useDeleteSubObjective();
	const updateStatusMutation = useUpdateTaskStatus();

	// ── Dialog state ───────────────────────────────────────────────────────────
	const [isCreateObjectiveOpen, setIsCreateObjectiveOpen] = useState(false);
	const [editingObjective, setEditingObjective] = useState<Objective | null>(
		null,
	);
	const [objectiveToDelete, setObjectiveToDelete] = useState<Objective | null>(
		null,
	);

	const [createSubParentId, setCreateSubParentId] = useState<string | null>(
		null,
	);
	const [editingSubObjective, setEditingSubObjective] =
		useState<SubObjective | null>(null);
	const [subObjectiveToDelete, setSubObjectiveToDelete] =
		useState<SubObjective | null>(null);

	const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
	const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
	// Context defaults handed to the create-task dialog (selection + date); the
	// dialog owns the form and seeds itself from these on open.
	const [taskCreateDefaults, setTaskCreateDefaults] =
		useState<TaskCreateDefaults>({
			objectiveId: "",
			subObjectiveId: "",
			scheduledDate: new Date().toISOString().split("T")[0],
		});
	const [editingTask, setEditingTask] = useState<Task | null>(null);

	// The farm the create-objective form defaults to (top-nav farm in single
	// scope, empty in All Farms — an explicit choice).
	const scopedFarmId = scope.kind === "single" ? scope.establishmentId : "";

	// ── Open helpers ───────────────────────────────────────────────────────────
	const openCreateObjective = () => setIsCreateObjectiveOpen(true);

	const openCreateTask = (sel: TreeSelection = selection, date?: Date) => {
		setTaskCreateDefaults({
			objectiveId: sel.kind === "all" ? "" : sel.objectiveId,
			subObjectiveId: sel.kind === "subObjective" ? sel.subObjectiveId : "",
			scheduledDate: (date ?? new Date()).toISOString().split("T")[0],
		});
		setIsCreateTaskOpen(true);
	};

	const openCreateSubObjective = (objectiveId: string) => {
		setCreateSubParentId(objectiveId);
		setExpanded((prev) => new Set(prev).add(objectiveId));
	};

	// Open the matching create dialog when deep-linked from the quick-capture FAB
	// (?create=task|objective), then clear the param so it doesn't re-fire.
	useEffect(() => {
		if (!search.create) return;
		if (search.create === "task") {
			setTaskCreateDefaults({
				objectiveId: objectiveParam ?? "",
				subObjectiveId: subObjectiveParam ?? "",
				scheduledDate: new Date().toISOString().split("T")[0],
			});
			setIsCreateTaskOpen(true);
		} else {
			setIsCreateObjectiveOpen(true);
		}
		navigate({
			to: "/app/org/$orgId/planning",
			params: { orgId },
			search: (prev) => ({ ...prev, create: undefined }),
			replace: true,
		});
	}, [search.create, objectiveParam, subObjectiveParam, navigate, orgId]);

	// ── Submit handlers ────────────────────────────────────────────────────────
	const handleDeleteObjective = async () => {
		if (!objectiveToDelete) return;
		await deleteObjectiveMutation.mutateAsync({
			organizationId: orgId,
			objectiveId: objectiveToDelete.id,
		});
		if (selectedObjectiveId === objectiveToDelete.id) {
			handleSelect({ kind: "all" });
		}
		setObjectiveToDelete(null);
	};

	const handleDeleteSubObjective = async () => {
		if (!subObjectiveToDelete) return;
		await deleteSubObjectiveMutation.mutateAsync({
			organizationId: orgId,
			subObjectiveId: subObjectiveToDelete.id,
		});
		if (
			selection.kind === "subObjective" &&
			selection.subObjectiveId === subObjectiveToDelete.id
		) {
			handleSelect({
				kind: "objective",
				objectiveId: subObjectiveToDelete.objectiveId,
			});
		}
		setSubObjectiveToDelete(null);
	};

	const handleStatusChange = async (taskId: string, newStatus: TaskStatus) => {
		await updateStatusMutation.mutateAsync({
			organizationId: orgId,
			taskId,
			data: { status: newStatus },
		});
	};

	// ── Derived display ────────────────────────────────────────────────────────
	const selectedObjective =
		selection.kind === "all"
			? undefined
			: objectives.find((o) => o.id === selection.objectiveId);
	const selectedSubObjective =
		selection.kind === "subObjective"
			? (subObjectivesByObjective[selection.objectiveId] ?? []).find(
					(s) => s.id === selection.subObjectiveId,
				)
			: undefined;
	const scopeLabel =
		selection.kind === "all"
			? t("planning.hierarchy.allTasks")
			: (selectedSubObjective?.name ??
				selectedObjective?.name ??
				t("planning.hierarchy.allTasks"));

	// Parent objective names for the sub-objective panel breadcrumbs.
	const createSubParentName = createSubParentId
		? objectives.find((o) => o.id === createSubParentId)?.name
		: undefined;
	const editingSubParentName = editingSubObjective
		? objectives.find((o) => o.id === editingSubObjective.objectiveId)?.name
		: undefined;
	// Top-level objective name for the edit-task breadcrumb, resolving both the
	// 2-layer (objectiveId) and 3-layer (subObjectiveId → parent) attachments.
	const editTaskObjectiveName =
		editingTask?.objective?.name ??
		(editingTask?.objectiveId
			? objectiveNameMap[editingTask.objectiveId]
			: undefined) ??
		(editingTask?.subObjectiveId
			? objectiveNameMap[subToObjective[editingTask.subObjectiveId]]
			: undefined);
	// Its id, resolved the same two ways — the breadcrumb needs it to navigate.
	const editTaskObjectiveId =
		editingTask?.objectiveId ??
		(editingTask?.subObjectiveId
			? subToObjective[editingTask.subObjectiveId]
			: undefined);

	const treeProps = {
		objectives,
		subObjectivesByObjective,
		totalTaskCount: tasks.length,
		objectiveTaskCounts,
		subObjectiveTaskCounts,
		tasksBySubObjective,
		directTasksByObjective,
		selection,
		onSelect: handleSelect,
		expanded,
		onToggleExpand: handleToggleExpand,
		farmNameMap,
		showFarmLabel: isAllFarms(scope),
		onCreateObjective: openCreateObjective,
		onCreateSubObjective: openCreateSubObjective,
		onAddTask: (sel: TreeSelection) => openCreateTask(sel),
		onTaskClick: (task: Task) => {
			setEditingTask(task);
			setIsTreeOpen(false);
		},
		onEditObjective: (o: Objective) => setEditingObjective(o),
		onDeleteObjective: (o: Objective) => setObjectiveToDelete(o),
		onEditSubObjective: (s: SubObjective) => setEditingSubObjective(s),
		onDeleteSubObjective: (s: SubObjective) => setSubObjectiveToDelete(s),
		isLoading: objectivesLoading,
	};

	const renderView = () => {
		if (tasksLoading) {
			return (
				<div className="flex items-center justify-center py-16">
					<span className="loading loading-spinner loading-lg text-primary" />
				</div>
			);
		}
		if (view === "list") {
			return (
				<TaskList
					tasks={filteredTasks}
					onTaskClick={(task) => setEditingTask(task)}
					onCreateTask={() => openCreateTask(selection)}
					farmNameMap={isAllFarms(scope) ? farmNameMap : undefined}
					emptyMessage={t("planning.tasks.emptyInScope")}
				/>
			);
		}
		if (view === "calendar") {
			return (
				<TaskCalendar
					tasks={filteredTasks}
					onTaskClick={(task) => setEditingTask(task)}
					onDateClick={(date) => openCreateTask(selection, date)}
				/>
			);
		}
		return (
			<TaskKanban
				tasks={filteredTasks}
				onStatusChange={handleStatusChange}
				onTaskClick={(task) => setEditingTask(task)}
				isUpdating={updateStatusMutation.isPending}
				paddockNameMap={paddockNameMap}
				farmNameMap={farmNameMap}
				objectiveNameMap={objectiveNameMap}
			/>
		);
	};

	return (
		<div className="flex flex-col gap-4 sm:gap-6">
			{/* Page header. The title names the section the way the nav does, and
			    carries the one create action for the whole workspace — it used to
			    live inside the list body, so it existed only in the list view. */}
			<div className="flex min-h-8 items-center justify-between gap-2">
				<h1 className="min-w-0 truncate font-bold text-[12px] text-base-content/70 uppercase tracking-[0.14em]">
					{t("common.planning")}
				</h1>
				<Button
					size="sm"
					onClick={() => setIsCreateMenuOpen(true)}
					className="h-7 shrink-0 gap-1 px-2 text-[12px] md:h-9 md:gap-2 md:px-4 md:text-sm"
				>
					<Plus className="h-3.5 w-3.5 md:h-4 md:w-4" />
					{t("common.create")}
				</Button>
			</div>
			<div
				className={cn(
					"grid gap-4 lg:gap-6 lg:items-start",
					railCollapsed
						? "lg:grid-cols-1"
						: "lg:grid-cols-[clamp(15rem,22vw,19rem)_minmax(0,1fr)]",
				)}
			>
				{/* Desktop hierarchy rail */}
				{!railCollapsed && (
					<aside className="hidden lg:sticky lg:top-4 lg:block">
						<PlanningHierarchyTree
							{...treeProps}
							onCollapse={() => setRailCollapsed(true)}
						/>
					</aside>
				)}

				{/* Work pane */}
				<section className="flex min-w-0 flex-col gap-4">
					{/* Mobile scope trigger → opens the tree as a drawer */}
					<button
						type="button"
						onClick={() => setIsTreeOpen(true)}
						className="flex items-center justify-between gap-2 rounded-xl border border-base-300 bg-base-100 px-3 py-2.5 text-left lg:hidden"
					>
						<span className="flex min-w-0 items-center gap-2">
							<Layers className="h-4 w-4 shrink-0 text-primary" />
							<span className="min-w-0">
								<span className="block text-[11px] text-base-content/50">
									{t("planning.hierarchy.browse")}
								</span>
								<span className="block truncate text-sm font-medium text-base-content">
									{scopeLabel}
								</span>
							</span>
						</span>
						<ChevronRight className="h-4 w-4 shrink-0 text-base-content/40" />
					</button>

					{/* Breadcrumb + count + view switcher */}
					<div className="flex items-start justify-between gap-3">
						<div className="flex min-w-0 items-start gap-2">
							{railCollapsed && (
								<button
									type="button"
									onClick={() => setRailCollapsed(false)}
									title={t("planning.hierarchy.title")}
									className="hidden shrink-0 items-center gap-1.5 rounded-lg border border-base-300 bg-base-100 px-2.5 py-2 text-sm font-medium text-base-content/70 transition-colors hover:text-primary lg:inline-flex"
								>
									<PanelLeftOpen className="h-4 w-4" />
									<span>{t("planning.hierarchy.title")}</span>
								</button>
							)}
							<div className="min-w-0">
								{/* Inline flow (not flex) so the trail wraps like text and the
								    ellipsis lands once, at the end of the third line, instead of
								    truncating every level to a fixed width. */}
								<nav
									aria-label="Breadcrumb"
									className="line-clamp-3 break-words text-sm leading-snug"
								>
									<button
										type="button"
										onClick={() => handleSelect({ kind: "all" })}
										className={cn(
											"inline text-left transition-colors hover:text-primary",
											selection.kind === "all"
												? "font-semibold text-base-content"
												: "text-base-content/60",
										)}
									>
										{t("planning.hierarchy.allTasks")}
									</button>
									{selectedObjective && (
										<>
											<ChevronRight className="mx-0.5 inline h-3.5 w-3.5 align-[-0.15em] text-base-content/40" />
											<button
												type="button"
												onClick={() =>
													handleSelect({
														kind: "objective",
														objectiveId: selectedObjective.id,
													})
												}
												className={cn(
													"inline text-left transition-colors hover:text-primary",
													selection.kind === "objective"
														? "font-semibold text-base-content"
														: "text-base-content/60",
												)}
											>
												{selectedObjective.name}
											</button>
										</>
									)}
									{selectedSubObjective && (
										<>
											<ChevronRight className="mx-0.5 inline h-3.5 w-3.5 align-[-0.15em] text-base-content/40" />
											<span className="font-semibold text-base-content">
												{selectedSubObjective.name}
											</span>
										</>
									)}
								</nav>
								<p className="mt-0.5 text-xs text-base-content/55">
									{t("planning.tasks.count", { count: filteredTasks.length })}
								</p>
							</div>
						</div>

						<PlanningViewMenu
							view={view}
							onChange={handleViewChange}
							className="shrink-0 self-start sm:hidden"
						/>
						<PlanningViewSwitcher
							view={view}
							onChange={handleViewChange}
							className="hidden shrink-0 self-start sm:inline-flex"
						/>
					</div>

					{renderView()}
				</section>
			</div>

			{/* Mobile hierarchy drawer */}
			<Sheet open={isTreeOpen} onOpenChange={setIsTreeOpen}>
				<SheetContent side="left" className="w-[88%] max-w-sm gap-0 p-0">
					<SheetTitle className="sr-only">
						{t("planning.hierarchy.title")}
					</SheetTitle>
					<div className="overflow-y-auto p-3 pt-12">
						<PlanningHierarchyTree {...treeProps} />
					</div>
				</SheetContent>
			</Sheet>

			{/* The header's one create action asks which layer first. */}
			<PlanningCreateSheet
				open={isCreateMenuOpen}
				onOpenChange={setIsCreateMenuOpen}
				objectives={objectives}
				farmNameMap={isAllFarms(scope) ? farmNameMap : undefined}
				selectedObjectiveId={
					selection.kind === "all" ? undefined : selection.objectiveId
				}
				onCreateObjective={openCreateObjective}
				onCreateSubObjective={openCreateSubObjective}
				onCreateTask={() => openCreateTask(selection)}
			/>

			<ObjectiveCreateDialog
				orgId={orgId}
				open={isCreateObjectiveOpen}
				onOpenChange={setIsCreateObjectiveOpen}
				farms={farmsData?.data ?? []}
				defaultFarmId={scopedFarmId}
			/>

			<ObjectiveEditDialog
				orgId={orgId}
				objective={editingObjective}
				onClose={() => setEditingObjective(null)}
			/>

			<SubObjectiveCreateDialog
				orgId={orgId}
				parentId={createSubParentId}
				parentName={createSubParentName}
				onClose={() => setCreateSubParentId(null)}
			/>

			<SubObjectiveEditDialog
				orgId={orgId}
				subObjective={editingSubObjective}
				onClose={() => setEditingSubObjective(null)}
				parentName={editingSubParentName}
				onNavigateParent={(objectiveId) => {
					setEditingSubObjective(null);
					handleSelect({ kind: "objective", objectiveId });
				}}
			/>

			<TaskCreateDialog
				orgId={orgId}
				open={isCreateTaskOpen}
				onOpenChange={setIsCreateTaskOpen}
				objectives={objectives}
				defaults={taskCreateDefaults}
			/>

			<TaskEditDialog
				orgId={orgId}
				task={editingTask}
				onClose={() => setEditingTask(null)}
				objectiveName={editTaskObjectiveName}
				objectiveId={editTaskObjectiveId}
				// Following an ancestor leaves the task behind: the panel closes and
				// the workspace re-scopes to that level.
				onNavigate={(selection) => {
					setEditingTask(null);
					handleSelect(selection);
				}}
			/>

			<ConfirmDeleteDialog
				open={objectiveToDelete !== null}
				title={t("planning.hierarchy.deleteObjectiveTitle")}
				description={t("planning.hierarchy.deleteObjectiveDescription", {
					name: objectiveToDelete?.name ?? "",
				})}
				onCancel={() => setObjectiveToDelete(null)}
				onConfirm={handleDeleteObjective}
				isPending={deleteObjectiveMutation.isPending}
			/>

			<ConfirmDeleteDialog
				open={subObjectiveToDelete !== null}
				title={t("planning.hierarchy.deleteSubObjectiveTitle")}
				description={t("planning.hierarchy.deleteSubObjectiveDescription", {
					name: subObjectiveToDelete?.name ?? "",
				})}
				onCancel={() => setSubObjectiveToDelete(null)}
				onConfirm={handleDeleteSubObjective}
				isPending={deleteSubObjectiveMutation.isPending}
			/>
		</div>
	);
}
